import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertCircle, Send } from "lucide-react";
import {
  fetchPublicForm,
  submitPublicLead,
  trackPublicView,
  getUtmsFromUrl,
} from "../../lib/publicLeadApi";
import { validateLead } from "../../lib/leadValidation";
import PublicLeadFields from "./PublicLeadFields";
import PublicLeadPaused from "./PublicLeadPaused";

const EMPTY_VALUES = {
  nombre: "",
  telefono: "",
  email: "",
  servicio: "",
  como_conocio: "",
  observaciones: "",
  _hp: "",
};

export default function PublicLeadForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [values, setValues] = useState(EMPTY_VALUES);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);

    fetchPublicForm(slug)
      .then((data) => {
        if (!alive) return;
        if (!data) {
          setLoadError("Este enlace no existe o ya no está activo.");
        } else {
          setForm(data);
          if (data.is_active) trackPublicView(slug);
        }
      })
      .catch(() => {
        if (alive) setLoadError("No se pudo cargar el formulario.");
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    document.title = "Entrena Tu Centro";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError(null);

    const clientError = validateLead(values, form);
    if (clientError) {
      setSubmitError(clientError);
      return;
    }

    setSubmitting(true);

    const payload = {
      slug,
      nombre: values.nombre,
      telefono: values.telefono,
      email: values.email,
      servicio: values.servicio,
      como_conocio: values.como_conocio,
      observaciones: values.observaciones,
      _hp: values._hp,
      utm: getUtmsFromUrl(location.search),
      referrer: document.referrer || null,
    };

    const { ok, body } = await submitPublicLead(payload);

    if (!ok) {
      setSubmitError(body?.message || "No se pudo enviar. Inténtalo de nuevo.");
      setSubmitting(false);
      return;
    }

    if (body?.redirect_url) {
      window.location.href = body.redirect_url;
      return;
    }

    navigate(`/l/${slug}/gracias`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            Enlace no disponible
          </h1>
          <p className="text-sm text-gray-600">{loadError}</p>
        </div>
      </div>
    );
  }

  if (form && (!form.is_active || form.owner_access_active === false)) {
    return <PublicLeadPaused form={form} />;
  }

  const color = form.primary_color || "#10b981";

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:py-12">
      <div className="max-w-md mx-auto">
        <div
          className="bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-in"
        >
          <div
            className="h-2"
            style={{ backgroundColor: color }}
          />

          <div className="p-6 sm:p-8">
            {form.logo_url && (
              <img
                src={form.logo_url}
                alt=""
                className="h-12 mb-4 object-contain"
              />
            )}

            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
              {form.title}
            </h1>
            {form.subtitle && (
              <p className="text-sm text-gray-600 mt-1.5">{form.subtitle}</p>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {/* Honeypot oculto: bots lo rellenan, humanos no lo ven */}
              <input
                type="text"
                name="_hp"
                value={values._hp}
                onChange={(e) =>
                  setValues({ ...values, _hp: e.target.value })
                }
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                }}
              />

              <PublicLeadFields
                form={form}
                values={values}
                onChange={setValues}
                focusColor={color}
              />

              {submitError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-medium shadow-sm hover:opacity-95 active:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
                style={{ backgroundColor: color }}
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Enviar
                  </>
                )}
              </button>

              {(() => {
                const privacyHref = form.privacy_url || `/l/${slug}/privacidad`;
                const isExternal = !!form.privacy_url;
                const responsable =
                  form.privacy_responsable || form.student_name;
                return (
                  <p className="text-xs text-gray-500 text-center pt-2">
                    Al enviar aceptas la{" "}
                    <a
                      href={privacyHref}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      className="underline hover:text-gray-700"
                    >
                      política de privacidad
                    </a>
                    {responsable && (
                      <>
                        .<br />
                        Responsable: {responsable}
                      </>
                    )}
                  </p>
                );
              })()}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
